import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabDescriptor, CommandeMS } from '../types';
import { buildCommandeMsTabs, computeDefaultTabIndex } from '../domain/commandeMsTabs';

// Hook mince : la logique (gating / onglet par defaut) vit dans domain/commandeMsTabs.
// Ici, uniquement le cablage de l'onglet actif a l'URL (?tab=<index>), comme la case BC presta.
export const useCommandeMsTabs = (commande: CommandeMS | null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const descriptors = useMemo<TabDescriptor[]>(
    () => (commande ? buildCommandeMsTabs(commande) : []),
    [commande],
  );

  const defaultIndex = useMemo(
    () => (commande ? computeDefaultTabIndex(descriptors, commande) : 0),
    [descriptors, commande],
  );

  const tabParam = searchParams.get('tab');
  const activeIndex = useMemo(() => {
    if (tabParam !== null) {
      const parsed = parseInt(tabParam, 10);
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < descriptors.length) {
        const target = descriptors[parsed];
        if (target && !target.disabled && !target.inactive) return parsed;
      }
      // Fallback par KIND (ex. ?tab=notifications ou ?tab=candidature depuis la liste), comme cote
      // acheteur. Meme garde disabled/inactive : on ne route pas vers un onglet grise.
      const byKind = descriptors.findIndex(
        (d) => d.kind === tabParam && !d.disabled && !d.inactive,
      );
      if (byKind >= 0) return byKind;
    }
    return defaultIndex;
  }, [tabParam, defaultIndex, descriptors]);

  const setActiveIndex = useCallback(
    (index: number) => {
      const target = descriptors[index];
      if (!target || target.disabled || target.inactive) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', String(index));
      setSearchParams(next, { replace: true });
    },
    [descriptors, searchParams, setSearchParams],
  );

  return {
    descriptors,
    activeIndex,
    activeTab: descriptors[activeIndex] ?? null,
    setActiveIndex,
  };
};
